(function (scope, factory) {
  'use strict';
  var api = factory();
  scope.RwadyAccountApi = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
}(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function normalizeDigits(value) {
    var arabic = '٠١٢٣٤٥٦٧٨٩';
    var persian = '۰۱۲۳۴۵۶۷۸۹';
    return String(value || '').replace(/[٠-٩]/g, function (digit) {
      return String(arabic.indexOf(digit));
    }).replace(/[۰-۹]/g, function (digit) {
      return String(persian.indexOf(digit));
    });
  }

  function normalizePhone(value) {
    var digits = normalizeDigits(value).replace(/\D/g, '');
    if (digits.indexOf('00964') === 0) digits = '0' + digits.slice(5);
    else if (digits.indexOf('964') === 0) digits = '0' + digits.slice(3);
    return /^07\d{9}$/.test(digits) ? digits : '';
  }

  function alternatePhone(value, primaryPhone) {
    var raw = String(value || '').trim();
    if (!raw) return { state: 'empty', phone: '' };
    var phone = normalizePhone(raw);
    if (!phone) return { state: 'invalid', phone: '' };
    if (phone === normalizePhone(primaryPhone)) return { state: 'primary', phone: '' };
    return { state: 'valid', phone: phone };
  }

  function secureIdempotencyKey(cryptoObject) {
    var cryptoApi = cryptoObject || (typeof crypto !== 'undefined' ? crypto : null);
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
      throw new Error('SECURE_RANDOM_UNAVAILABLE');
    }
    if (typeof cryptoApi.randomUUID === 'function') return 'rwady-address-' + cryptoApi.randomUUID();
    var bytes = new Uint8Array(24);
    cryptoApi.getRandomValues(bytes);
    return 'rwady-address-' + Array.prototype.map.call(bytes, function (byte) {
      return byte.toString(16).padStart(2, '0');
    }).join('');
  }

  function secureDeletionIdempotencyKey(cryptoObject) {
    var cryptoApi = cryptoObject || (typeof crypto !== 'undefined' ? crypto : null);
    if (!cryptoApi || typeof cryptoApi.getRandomValues !== 'function') {
      throw new Error('SECURE_RANDOM_UNAVAILABLE');
    }
    if (typeof cryptoApi.randomUUID === 'function') return 'rwady-account-deletion-' + cryptoApi.randomUUID();
    var bytes = new Uint8Array(24);
    cryptoApi.getRandomValues(bytes);
    return 'rwady-account-deletion-' + Array.prototype.map.call(bytes, function (byte) {
      return byte.toString(16).padStart(2, '0');
    }).join('');
  }

  function shopifyVariantId(value) {
    var match = String(value || '').trim().match(/(?:^|\/)\s*(\d+)$/);
    return match ? match[1] : '';
  }

  function isCompletedOrder(order) {
    return String(order && order.fulfillment_status || '').trim().toUpperCase().replace(/[\s-]+/g, '_') === 'FULFILLED';
  }

  function canReorderOrder(order) {
    var tracking = String(order && order.tracking_status || '');
    if (tracking) return tracking === 'delivered' || tracking === 'cancelled';
    return isCompletedOrder(order);
  }

  function buildReorderPlan(order, productsByHandle) {
    if (!canReorderOrder(order)) return { items: [], skipped: [] };
    return buildItemsReorderPlan(order && order.items, productsByHandle);
  }

  // A per-product reorder is a new cart addition, independent of the old
  // order's fulfillment state. Current variant availability still governs it.
  function buildLineReorderPlan(item, productsByHandle) {
    return buildItemsReorderPlan([item], productsByHandle);
  }

  function buildItemsReorderPlan(items, productsByHandle) {
    var products = productsByHandle || {};
    var quantities = {};
    var currentPrices = {};
    var skipped = [];
    (Array.isArray(items) ? items : []).forEach(function (item) {
      var handle = String(item && item.product_handle || '').trim();
      var variantId = shopifyVariantId(item && item.variant_id);
      var quantity = Number(item && item.quantity);
      var product = handle ? products[handle] : null;
      var variants = Array.isArray(product && product.variants) ? product.variants : [];
      var variant = variants.find(function (candidate) {
        return String(candidate && candidate.id || '') === variantId;
      });
      var currentPriceValue = variant && variant.price;
      var currentPrice = Number(currentPriceValue);
      if (!handle || !variantId || !Number.isSafeInteger(quantity) || quantity < 1 || !variant || variant.available !== true || currentPriceValue === null || currentPriceValue === '' || !Number.isFinite(currentPrice) || currentPrice < 0) {
        skipped.push(String(item && item.product_title || item && item.variant_title || '').trim() || variantId || handle || 'unavailable');
        return;
      }
      quantities[variantId] = (quantities[variantId] || 0) + quantity;
      currentPrices[variantId] = currentPrice;
    });
    return {
      items: Object.keys(quantities).map(function (variantId) {
        return { id: variantId, quantity: quantities[variantId], current_price: currentPrices[variantId] };
      }),
      skipped: skipped
    };
  }

  function accountLinkState(profilePayload, addressPayload) {
    var states = [profilePayload, addressPayload].map(function (payload) {
      return String(payload && payload.shopify_link_state || '').trim().toLowerCase();
    }).filter(Boolean);
    if (!states.length || states.every(function (state) { return state === 'linked'; })) return 'linked';
    if (states.indexOf('ambiguous') !== -1) return 'ambiguous';
    if (states.indexOf('retry') !== -1) return 'retry';
    if (states.indexOf('pending') !== -1) return 'pending';
    return 'unknown';
  }

  function customerShipmentStatus(value, orderLevel) {
    var known = ['ready', 'shipped', 'in_transit', 'delivered', 'cancelled'];
    if (orderLevel) known = known.concat(['awaiting_confirmation', 'confirmed']);
    return known.indexOf(value) !== -1 ? value : null;
  }

  // The Worker validates ownership and quantities. Fall back to ungrouped
  // historical rows for older Workers or missing snapshots.
  function buildOrderGroups(order) {
    var items = Array.isArray(order && order.items) ? order.items : [];
    var snapshot = order && order.line_fulfillment;
    var lines = new Map((snapshot && snapshot.lines || []).map(function (line) { return [line.line_item_id, line]; }));
    var byId = new Map(items.map(function (item) { return [item.line_item_id, item]; }));
    var used = new Map();
    var groups = [];
    (snapshot && snapshot.shipments || []).forEach(function (shipment) {
      var entries = [];
      shipment.items.forEach(function (allocation) {
        var item = byId.get(allocation.line_item_id);
        if (!item) return;
        used.set(item.line_item_id, (used.get(item.line_item_id) || 0) + allocation.quantity);
        entries.push({ item: item, quantity: allocation.quantity, line: lines.get(item.line_item_id) || null,
          status: customerShipmentStatus(shipment.customer_status) || shipment.status });
      });
      if (entries.length) groups.push({ shipment: shipment, entries: entries });
    });
    var remaining = [];
    items.forEach(function (item) {
      var quantity = Number(item.quantity) - (used.get(item.line_item_id) || 0);
      var line = lines.get(item.line_item_id);
      if (quantity > 0) remaining.push({
        item: item, quantity: quantity, line: line || null,
        status: !line ? 'unknown' : line.status === 'cancelled' || line.customer_status === 'cancelled' ? 'cancelled' :
          line.status === 'confirmed' ? 'confirmed' : 'awaiting_shipment'
      });
    });
    var cancelledParcels = remaining.filter(function (entry) {
      return entry.line && (entry.line.removal_reason === 'after_shipment' && entry.status === 'cancelled' ||
        entry.line.customer_status === 'cancelled' && entry.line.status !== 'cancelled');
    });
    remaining = remaining.filter(function (entry) { return cancelledParcels.indexOf(entry) === -1; });
    if (cancelledParcels.length) groups.push({ shipment: null, kind: 'cancelled_shipment', entries: cancelledParcels });
    if (remaining.length) groups.push({ shipment: null, kind: 'unprepared', entries: remaining });
    return groups;
  }

  function orderLineSummary(order) {
    var items = order && order.items || [];
    var snapshot = order && order.line_fulfillment;
    if (!snapshot || !snapshot.updated_at || !items.length || order.has_more_items) {
      return order && order.tracking_status === 'cancelled' ? 'cancelled' : null;
    }
    var lines = snapshot.lines || [];
    var complete = items.every(function (item) {
      return lines.some(function (line) { return line.line_item_id === item.line_item_id; });
    });
    var customerStatus = complete && customerShipmentStatus(snapshot.customer_status, true);
    if (customerStatus) return customerStatus;
    if (order && order.tracking_status === 'cancelled') return 'cancelled';
    var active = lines.filter(function (line) { return line.status !== 'cancelled'; });
    if (complete && !active.length) return 'cancelled';
    if (complete && active.length && active.every(function (line) { return line.quantity > 0 && line.delivered === line.quantity; })) return 'delivered';
    if (active.some(function (line) { return line.delivered > 0; })) return 'partially_delivered';
    if (complete && active.length && active.every(function (line) { return line.status === 'returned'; })) return 'returned';
    if (active.some(function (line) { return line.shipped > 0; })) {
      if (!complete || active.some(function (line) { return line.shipped < line.quantity; })) return 'partially_shipped';
      return active.every(function (line) { return line.status === 'in_transit'; }) ? 'in_transit' : 'shipped';
    }
    if (active.some(function (line) { return line.allocated > 0; })) return 'reserved';
    return complete && active.every(function (line) { return line.status === 'confirmed'; }) ? 'confirmed' : null;
  }

  function createAccountRefreshController(options) {
    var settings = options || {};
    if (typeof settings.read !== 'function') throw new Error('ACCOUNT_REFRESH_READER_REQUIRED');
    var schedule = settings.schedule || function (callback, delay) { return setTimeout(callback, delay); };
    var cancel = settings.cancel || function (timer) { clearTimeout(timer); };
    var delays = Array.isArray(settings.delays) ? settings.delays.slice() : [
      500, 1000, 2000, 4000, 8000, 15000,
      30000, 30000, 30000, 30000, 30000, 30000, 30000, 30000, 30000, 30000
    ];
    var timer = null;
    var generation = 0;
    var stopped = false;
    var presentedState = '';

    function clearScheduledRefresh() {
      if (timer === null) return;
      cancel(timer);
      timer = null;
    }

    function notifyError(error) {
      if (typeof settings.onError === 'function') settings.onError(error);
      return { state: 'error', error: error };
    }

    function makeSnapshot(linkState, profilePayload, addressPayload, receiptPayload) {
      return {
        state: linkState,
        profile: profilePayload.customer || {},
        orders: Array.isArray(profilePayload.orders) ? profilePayload.orders : [],
        addresses: Array.isArray(addressPayload.addresses) ? addressPayload.addresses : [],
        receipts: Array.isArray(receiptPayload && receiptPayload.receipts) ? receiptPayload.receipts : [],
        receipts: Array.isArray(receiptPayload && receiptPayload.receipts) ? receiptPayload.receipts : []
      };
    }

    function presentSnapshot(snapshot) {
      if (snapshot.state !== 'linked' && presentedState === snapshot.state) return;
      presentedState = snapshot.state;
      if (typeof settings.onLinked === 'function') settings.onLinked(snapshot);
    }

    function scheduleRetry(activeGeneration, attempt, error) {
      if (attempt >= delays.length) return notifyError(error);
      if (typeof settings.onWaiting === 'function') settings.onWaiting({ state: 'retry', attempt: attempt, error: error });
      timer = schedule(function () {
        timer = null;
        return readGeneration(activeGeneration, attempt + 1);
      }, Math.max(0, Number(delays[attempt]) || 0));
      return { state: 'retry', attempt: attempt, error: error };
    }

    function readGeneration(activeGeneration, attempt) {
      if (stopped || activeGeneration !== generation) return Promise.resolve({ state: 'stale' });
      return Promise.resolve().then(settings.read).then(function (results) {
        if (stopped || activeGeneration !== generation) return { state: 'stale' };
        var profilePayload = results && results[0] || {};
        var addressPayload = results && results[1] || {};
        var linkState = accountLinkState(profilePayload, addressPayload);
        var receiptPayload = results && results[2] || {};
        var snapshot = makeSnapshot(linkState, profilePayload, addressPayload, receiptPayload);
        if (linkState === 'linked') {
          clearScheduledRefresh();
          presentSnapshot(snapshot);
          if (Number(settings.refreshInterval) > 0) {
            timer = schedule(function () {
              timer = null;
              return readGeneration(activeGeneration, 0);
            }, Math.max(10000, Number(settings.refreshInterval)));
          }
          return snapshot;
        }
        if (linkState === 'pending' || linkState === 'retry') {
          if (typeof settings.onLinked === 'function') presentSnapshot(snapshot);
          else if (typeof settings.onWaiting === 'function') settings.onWaiting({ state: linkState, attempt: attempt });
          if (attempt >= delays.length) {
            clearScheduledRefresh();
            return snapshot;
          }
          timer = schedule(function () {
            timer = null;
            return readGeneration(activeGeneration, attempt + 1);
          }, Math.max(0, Number(delays[attempt]) || 0));
          return snapshot;
        }
        if (linkState === 'ambiguous') {
          clearScheduledRefresh();
          presentSnapshot(snapshot);
          return snapshot;
        }
        var stateError = new Error('ACCOUNT_LINK_STATE_INVALID');
        stateError.code = stateError.message;
        return notifyError(stateError);
      }).catch(function (error) {
        if (stopped || activeGeneration !== generation) return { state: 'stale' };
        if (error && error.status === 401) return notifyError(error);
        return scheduleRetry(activeGeneration, attempt, error);
      });
    }

    function start() {
      stopped = false;
      generation += 1;
      presentedState = '';
      clearScheduledRefresh();
      return readGeneration(generation, 0);
    }

    function stop() {
      stopped = true;
      generation += 1;
      presentedState = '';
      clearScheduledRefresh();
    }

    return { start: start, retry: start, stop: stop };
  }

  function ApiError(response, payload) {
    this.name = 'RwadyAccountApiError';
    this.message = payload && (payload.message || payload.code) || 'ACCOUNT_REQUEST_FAILED';
    this.status = response ? response.status : 0;
    this.code = payload && payload.code || '';
    this.retryAfter = Number(payload && payload.retry_after) || 0;
    this.payload = payload || null;
  }
  ApiError.prototype = Object.create(Error.prototype);
  ApiError.prototype.constructor = ApiError;

  function createClient(options) {
    var settings = options || {};
    var baseUrl = String(settings.baseUrl || '').replace(/\/$/, '');
    var fetchImpl = settings.fetchImpl || (typeof fetch === 'function' ? fetch.bind(globalThis) : null);
    if (!baseUrl || !fetchImpl) throw new Error('ACCOUNT_API_CONFIGURATION_REQUIRED');

    function request(path, requestOptions) {
      var input = requestOptions || {};
      var headers = Object.assign({ Accept: 'application/json' }, input.headers || {});
      var body = input.body;
      if (body !== undefined && body !== null && typeof body !== 'string' && !input.rawBody) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify(body);
      }
      return fetchImpl(baseUrl + path, {
        method: input.method || 'GET',
        credentials: 'include',
        cache: 'no-store',
        headers: headers,
        body: body === undefined ? undefined : body
      }).then(function (response) {
        return response.text().then(function (text) {
          var payload = {};
          try { payload = text ? JSON.parse(text) : {}; } catch (error) { payload = {}; }
          if (!response.ok) throw new ApiError(response, payload);
          return payload;
        });
      });
    }

    function requestAvatar() {
      return fetchImpl(baseUrl + '/account/avatar', {
        method: 'GET',
        credentials: 'include',
        cache: 'no-store',
        headers: { Accept: 'image/webp,image/jpeg,image/png' }
      }).then(function (response) {
        if (response.status === 404) return null;
        if (!response.ok) {
          return response.text().then(function (text) {
            var payload = {};
            try { payload = text ? JSON.parse(text) : {}; } catch (error) { payload = {}; }
            throw new ApiError(response, payload);
          });
        }
        return response.blob();
      });
    }

    return {
      session: function () { return request('/account/session'); },
      profile: function () { return request('/account/profile'); },
      receipts: function () { return request('/account/receipts'); },
      updateProfile: function (body) { return request('/account/profile', { method: 'POST', body: body }); },
      sendOtp: function (body) { return request('/account/auth/send', { method: 'POST', body: body }); },
      verifyOtp: function (body) { return request('/account/auth/verify', { method: 'POST', body: body }).then(function (result) { if (typeof document !== 'undefined') document.dispatchEvent(new CustomEvent('rwady:account-login')); return result; }); },
      authorizeCheckout: function (body) { return request('/account/checkout/authorize', { method: 'POST', body: body }); },
      uploadTransferReceipt: function (file, authorization, phone) {
        var form = new FormData();
        form.append('phone', phone);
        form.append('pre_checkout_id', authorization.pre_checkout_id);
        form.append('verification_token', authorization.verification_token);
        form.append('receipt', file, file.name || 'transfer-receipt');
        return request('/account/checkout/transfer-receipt', { method: 'POST', body: form, rawBody: true });
      },
      unlinkTransferReceipt: function (authorization, phone) {
        return request('/account/checkout/transfer-receipt', {
          method: 'DELETE',
          body: {
            phone: phone,
            pre_checkout_id: authorization.pre_checkout_id,
            verification_token: authorization.verification_token
          }
        });
      },
      logout: function () { return request('/account/logout', { method: 'POST', body: {} }); },
      startAccountDeletion: function () {
        return request('/account/deletion/start', { method: 'POST', body: {} });
      },
      confirmAccountDeletion: function (challengeId, code, idempotencyKey) {
        return request('/account/deletion/confirm', {
          method: 'POST',
          headers: { 'Idempotency-Key': idempotencyKey },
          body: { challenge_id: challengeId, code: code }
        });
      },
      addresses: function () { return request('/account/addresses'); },
      createAddress: function (body, idempotencyKey) {
        return request('/account/addresses', {
          method: 'POST',
          headers: { 'Idempotency-Key': idempotencyKey },
          body: body
        });
      },
      updateAddress: function (id, body, idempotencyKey) {
        var headers = idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
        return request('/account/addresses/' + encodeURIComponent(id), { method: 'PATCH', headers: headers, body: body });
      },
      setDefaultAddress: function (id) {
        return request('/account/addresses/' + encodeURIComponent(id) + '/default', { method: 'PATCH', body: {} });
      },
      deleteAddress: function (id) {
        return request('/account/addresses/' + encodeURIComponent(id), { method: 'DELETE' });
      },
      saveAlternatePhone: function (phone) {
        return request('/account/contact-phone', { method: 'PUT', body: { alternate_phone: phone } });
      },
      deleteAlternatePhone: function () {
        return request('/account/contact-phone', { method: 'DELETE' });
      },
      avatar: requestAvatar,
      uploadAvatar: function (blob, filename) {
        var form = new FormData();
        form.append('avatar', blob, filename || 'rwady-avatar.webp');
        return request('/account/avatar', { method: 'PUT', body: form, rawBody: true });
      },
      deleteAvatar: function () {
        return request('/account/avatar', { method: 'DELETE' });
      }
    };
  }

  return {
    ApiError: ApiError,
    createClient: createClient,
    normalizeDigits: normalizeDigits,
    normalizePhone: normalizePhone,
    alternatePhone: alternatePhone,
    accountLinkState: accountLinkState,
    createAccountRefreshController: createAccountRefreshController,
    secureIdempotencyKey: secureIdempotencyKey,
    secureDeletionIdempotencyKey: secureDeletionIdempotencyKey,
    shopifyVariantId: shopifyVariantId,
    isCompletedOrder: isCompletedOrder,
    canReorderOrder: canReorderOrder,
    buildReorderPlan: buildReorderPlan,
    buildLineReorderPlan: buildLineReorderPlan,
    buildOrderGroups: buildOrderGroups,
    orderLineSummary: orderLineSummary
  };
}));

